import React, { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { withRouter } from "react-router-dom";
import { injectIntl } from "react-intl";
import { Schema } from "rsuite";
import axios from "axios";
import { createGlobalStyle } from "styled-components";

import { compareObjects, servicePick, getServiceByLocation, resolvePerPage } from "utils/helpers";
import { readSavedFilters } from "utils/filters";
import { LS_DIALER_HOT_ACCESSES_PER_PAGE } from "const/localStorageKeys";
import {
    LOCAL_STORAGE_ACCESSES_FILTERS_DIALER,
    SCREEN_MEDIA,
    USD_DEFAULT_CURRENCY_KEY,
    transformed_service_data,
} from "const";
import { FlexGrid, FlexGridItem } from "components/base/FlexGrid";
import PageHeader from "components/base/PageHeader";
import { Spacer } from "components/base/Spacer";
import Page from "components/base/Page";
import m from "definedMessages";
import HotAccessesFilters from "./HotAccessesFilters";
import HotAccessesTable from "./HotAccessesTable";
import { withTranslation } from "hoc/WithTranslation";


const { md } = SCREEN_MEDIA;

const { StringType } = Schema.Types;

const MIN_LENGTH = 3;
const MAX_LENGTH = 40;

const normalizeSavedFilters = (saved, service) => {
    const cur1 = servicePick(service, "str", "origin");
    const cur2 = servicePick(service, "str2", "destination");

    const alt1 = cur1 === "str" ? "origin" : "str";
    const alt2 = cur2 === "str2" ? "destination" : "str2";

    const out = { ...saved };

    if (out[cur1] == null && out[alt1] != null) out[cur1] = out[alt1];
    if (out[cur2] == null && out[alt2] != null) out[cur2] = out[alt2];

    return out;
};

const hasModelError = (model, value) => {
    const checker = model.check(value);
    return Object.keys(checker).some(k => checker[k].hasError);
};


const HotAccesses = ({
    intl,

    accessesList,
    service,

    account,
    getTrunkList,
    setTrunkList,
    setDialerService,
    getHotAccessList,
    setEmptyHotAccessList,

    page,
    count,
    perPage,

    accessesLoading,

    accountDialerState,
    accountJoinList = [],

    formatMessage,
    ...props
}) => {
    const cancelRef = useRef(null);

    const accountInfo = useMemo(() => {
        return accountJoinList.find(a =>
            transformed_service_data[a?.service] === service
        ) || {};
    }, [service, accountJoinList]);

    const accountIsSingle = useMemo(() => accountJoinList.length === 1, [accountJoinList]);

    const effectiveService = useMemo(() => {
        return service === null ? getServiceByLocation(props.location) : service;
    }, [service, props.location]);

    const filtersModel = useMemo(() => Schema.Model({
        str: StringType()
            .minLength(MIN_LENGTH, formatMessage(m.minLength, { count: MIN_LENGTH }))
            .maxLength(MAX_LENGTH, formatMessage(m.maxLength, { count: MAX_LENGTH })),
        str2: StringType()
            .minLength(MIN_LENGTH, formatMessage(m.minLength, { count: MIN_LENGTH }))
            .maxLength(MAX_LENGTH, formatMessage(m.maxLength, { count: MAX_LENGTH })),
        origin: StringType()
            .minLength(MIN_LENGTH, formatMessage(m.minLength, { count: MIN_LENGTH }))
            .maxLength(MAX_LENGTH, formatMessage(m.maxLength, { count: MAX_LENGTH })),
        destination: StringType()
            .minLength(MIN_LENGTH, formatMessage(m.minLength, { count: MIN_LENGTH }))
            .maxLength(MAX_LENGTH, formatMessage(m.maxLength, { count: MAX_LENGTH })),
    }), [formatMessage]);

    const isTest = ("is_test" in accountInfo) ? accountInfo.is_test : false;

    const defaultFilter = useMemo(() => {
        return {
            [servicePick(service, "str", "origin")]: "",
            [servicePick(service, "str2", "destination")]: "",
            ...(service ? {} : { message: "" }),
            sp_key_list: null,
            cur_key: (isTest && accountInfo.cur_key) ? accountInfo.cur_key : USD_DEFAULT_CURRENCY_KEY,
        };
    }, [service, isTest, accountInfo.cur_key]);

    const computeInitialFilter = useCallback(() => {
        const savedRaw = readSavedFilters(LOCAL_STORAGE_ACCESSES_FILTERS_DIALER);
        const saved = normalizeSavedFilters(savedRaw, service);
        return { ...defaultFilter, ...saved };
    }, [service, defaultFilter]);

    const [isSupport, setIsSupport] = useState(null);
    const [filter, setFilter] = useState(() => computeInitialFilter());
    const filterRef = useRef(filter);
    const initFetchKeyRef = useRef(null);

    const requestItems = useCallback((_page, _perPage, nextFilter, isAuth = false) => {
        if (hasModelError(filtersModel, nextFilter)) {
            setEmptyHotAccessList();
            return;
        }

        if (cancelRef.current) {
            cancelRef.current.cancel("Operation canceled due to new request.");
        }

        cancelRef.current = axios.CancelToken.source();
        getHotAccessList(effectiveService, _perPage, nextFilter, _page, isAuth, cancelRef.current);
    }, [effectiveService, filtersModel, getHotAccessList, setEmptyHotAccessList]);

    const requestItemsRef = useRef(requestItems);

    const savedFilter = useMemo(() => computeInitialFilter(), [computeInitialFilter]);

    useEffect(() => {
        filterRef.current = filter;
    }, [filter]);

    useEffect(() => {
        requestItemsRef.current = requestItems;
    }, [requestItems]);

    useEffect(() => {
        return () => {
            if (cancelRef.current) {
                cancelRef.current.cancel("unmount");
            }
        };
    }, []);

    const resolvedPerPage = useMemo(() => resolvePerPage(
        LS_DIALER_HOT_ACCESSES_PER_PAGE,
        perPage,
    ), [perPage]);

    const getItems = useCallback((_page, _perPage = resolvedPerPage, isAuth = false, nextFilter) => {
        requestItems(_page, _perPage, nextFilter ?? filterRef.current, isAuth);
    }, [requestItems, resolvedPerPage]);

    useEffect(() => {
        if (effectiveService === null || effectiveService === undefined) return;

        const initFetchKey = `${effectiveService ?? ""}:${resolvedPerPage}`;
        if (initFetchKeyRef.current === initFetchKey) {
            return;
        }

        initFetchKeyRef.current = initFetchKey;

        const initFilter = computeInitialFilter();

        setFilter(prev => (compareObjects(prev, initFilter) ? prev : initFilter));

        getTrunkList(effectiveService);
        requestItemsRef.current(1, resolvedPerPage, initFilter, true);
    }, [
        accountInfo?.id,
        effectiveService,
        computeInitialFilter,
        getTrunkList,
        resolvedPerPage
    ]);

    useEffect(() => {
        const roleList = account?.session?.account_user?.role_list || [];
        setIsSupport(roleList.includes("Account Support"));
    }, [account]);

    const onChangeFilters = (patch) => {
        const next = { ...filter, ...patch };

        setFilter(next);
        localStorage.setItem(LOCAL_STORAGE_ACCESSES_FILTERS_DIALER, JSON.stringify(next));

        getItems(1, resolvedPerPage, false, next);
    };

    const headerTitle = formatMessage(m.hotAccessList);

    return (
        <Page>
            <GlobalStyle />
            <PageHeader title={headerTitle} />

            <FlexGrid style={{ maxWidth: "100%" }} justify="space-between">
                <FlexGridItem grow={1} style={{ maxWidth: "100%" }}>
                    <HotAccessesFilters
                        onChangeFilter={onChangeFilters}
                        loading={accessesLoading || false}
                        defaultFilter={defaultFilter}
                        savedFilter={savedFilter}
                        filtersModel={filtersModel}
                        {...{ service, accountInfo, accountDialerState }}
                    />
                </FlexGridItem>
            </FlexGrid>

            <Spacer />

            <HotAccessesTable
                data={accessesList}
                loading={accessesLoading || false}
                filter={filter}
                {...{
                    service,
                    accountInfo,
                    accountIsSingle,
                    isTest,
                    isSupport,
                    getItems,
                    setDialerService,
                    page,
                    count,
                    perPage,
                    accountDialerState
                }}
            />

        </Page>
    )
};


export default injectIntl(withRouter(withTranslation(HotAccesses)));


const GlobalStyle = createGlobalStyle`
    .rs-picker-check-menu-items {
        overflow-y: hidden !important;
        max-height: none !important;
    }
`;